import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, formatApiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Camera, Star } from "lucide-react";

export const Stars = ({ value, onPick, size = 18 }) => (
    <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
            <button
                key={i}
                type={onPick ? "button" : undefined}
                onClick={onPick ? () => onPick(i) : undefined}
                data-testid={onPick ? `review-star-${i}` : undefined}
                aria-label={`${i} Sterne`}
                className={onPick ? "transition-transform duration-150 hover:scale-125" : "cursor-default"}
            >
                <Star size={size} className={i <= value ? "fill-[#C88A45] text-[#C88A45]" : "text-[#2E2517]"} />
            </button>
        ))}
    </div>
);

export const Bewertungen = () => {
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
        load();
        window.addEventListener("reviews:updated", load);
        return () => window.removeEventListener("reviews:updated", load);
    }, []);

    return (
        <section id="bewertungen" className="mx-auto max-w-7xl px-6 py-24 lg:py-32" data-testid="bewertungen-section">
            <Reveal>
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Bewertungen</p>
                <h2 className="mb-6 font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl">
                    Was Kunden <span className="text-[#C88A45]">sagen.</span>
                </h2>
                <p className="mb-12 max-w-md text-sm leading-relaxed text-[#9C917D]">
                    Erfahrungen mit Holz, Lieferung und Kontakt — direkt von Kunden.
                </p>
            </Reveal>
            {reviews.length === 0 ? (
                <p className="rounded border border-[#2E2517] bg-[#1B150E] p-6 text-sm text-[#9C917D]">
                    Noch keine Bewertungen — Ihre Stimme könnte die erste sein. Formular ganz unten auf der Seite.
                </p>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-list">
                    {reviews.map((r) => (
                        <Reveal key={r.id}>
                            <div className="rounded border border-[#2E2517] bg-[#1B150E] p-5" data-testid={`review-${r.id}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-sm font-semibold text-[#EDE6DA]">{r.name}</p>
                                    <Stars value={r.rating} size={13} />
                                </div>
                                <p className="mt-2 text-sm leading-relaxed text-[#D9D2C4]">{r.text}</p>
                                {r.reply?.text && (
                                    <div className="mt-3 rounded border-l-2 border-[#C88A45] bg-[#120E09] p-3" data-testid={`review-reply-${r.id}`}>
                                        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#C88A45]">Antwort von KischioWood</p>
                                        <p className="mt-1 text-sm text-[#D9D2C4]">{r.reply.text}</p>
                                    </div>
                                )}
                                {r.photo_path && (
                                    <img
                                        src={`${API}/files/${r.photo_path}`}
                                        alt={`Foto zur Bewertung von ${r.name}`}
                                        className="img-warm mt-3 max-h-40 rounded border border-[#2E2517] object-cover"
                                    />
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
            )}
        </section>
    );
};

export const BewertungForm = () => {
    const [form, setForm] = useState({ name: "", rating: 5, text: "" });
    const [photo, setPhoto] = useState(null);
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            let photo_path = null;
            if (photo) {
                const fd = new FormData();
                fd.append("file", photo);
                const up = await api.post("/uploads/review-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                photo_path = up.data.storage_path;
            }
            await api.post("/reviews", { ...form, photo_path });
            toast.success("Vielen Dank für Ihre Bewertung!");
            setForm({ name: "", rating: 5, text: "" });
            setPhoto(null);
            window.dispatchEvent(new Event("reviews:updated"));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="border-t border-[#2E2517] bg-[#1B150E]" data-testid="review-form-section">
            <div className="mx-auto max-w-7xl px-6 py-12">
                <form onSubmit={submit} data-testid="review-form">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
                        <p className="font-display text-lg font-bold uppercase tracking-tight text-[#EDE6DA]">Bewerten Sie uns</p>
                        <Stars value={form.rating} onPick={(v) => setForm({ ...form, rating: v })} size={22} />
                        <Input
                            data-testid="review-name-input"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="h-10 w-full border-[#2E2517] bg-[#120E09] text-sm text-[#EDE6DA] sm:w-48"
                            placeholder="Ihr Name *"
                        />
                        <Input
                            data-testid="review-text-input"
                            required
                            value={form.text}
                            onChange={(e) => setForm({ ...form, text: e.target.value })}
                            className="h-10 w-full min-w-0 flex-1 border-[#2E2517] bg-[#120E09] text-sm text-[#EDE6DA] sm:min-w-64"
                            placeholder="Ihre Erfahrung in einem Satz *"
                        />
                        <label data-testid="review-photo-label" className="flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-[#2E2517] px-4 py-2 text-xs text-[#9C917D] transition-colors duration-200 hover:border-[#C88A45]/50 hover:text-[#EDE6DA]">
                            <Camera size={15} className="text-[#C88A45]" />
                            {photo ? photo.name.slice(0, 20) : "Foto (optional)"}
                            <input
                                type="file"
                                accept="image/*"
                                data-testid="review-photo-input"
                                className="hidden"
                                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                            />
                        </label>
                        <button
                            data-testid="review-submit-button"
                            type="submit"
                            disabled={sending}
                            className="rounded-full bg-[#C88A45] px-6 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-[background-color,opacity] duration-200 hover:bg-[#DBA057] disabled:opacity-50"
                        >
                            {sending ? "Senden …" : "Veröffentlichen"}
                        </button>
                    </div>
                    <p className="mt-3 text-xs text-[#9C917D]">Mit dem Absenden stimmen Sie der Veröffentlichung auf dieser Website zu.</p>
                </form>
            </div>
        </section>
    );
};
